import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  X, 
  Play, 
  Pause, 
  RotateCcw, 
  RotateCw, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Minimize, 
  SkipForward, 
  SkipBack, 
  List, 
  Zap, 
  AlertCircle,
  Sparkles,
  Lock,
  Unlock,
  PictureInPicture2,
  Volume1,
  Maximize2,
  Languages,
  Settings2,
  Moon,
  Clock,
  Check,
  Search,
  Subtitles,
  Upload,
  ExternalLink
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { MediaItem, EpisodeInfo, UserProfile, SubtitleCue } from '../types';
import { updateWatchProgress } from '../lib/firebase';
import { isBackEvent } from '../utils/deviceDetection';
import { SubdlSubtitleModal, parseClientSrtOrVtt } from './SubdlSubtitleModal';
import { buildApiUrl } from '../utils/apiConfig';
import { extractStreamUrl, playMediaUniversal } from '../utils/streamUtils';


interface VideoPlayerModalProps {
  item: MediaItem;
  initialEpisodeIndex?: number;
  initialTime?: number;
  onClose: () => void;
  user: UserProfile | null;
  isTv?: boolean;
}

type AspectRatioMode = 'contain' | 'cover' | 'fill';

export const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({
  item,
  initialEpisodeIndex = 0,
  initialTime = 0,
  onClose,
  user,
  isTv = false
}) => {
  const [episodeIndex, setEpisodeIndex] = useState(initialEpisodeIndex);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(initialTime || 0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [useFallbackStream, setUseFallbackStream] = useState(false);
  const [nextEpisodeCountdown, setNextEpisodeCountdown] = useState<number | null>(null);
  const [aspectRatio, setAspectRatio] = useState<AspectRatioMode>('contain');
  const [dialogueBoost, setDialogueBoost] = useState(false);

  // Audio Tracks & Subtitles State
  const [isAudioSubsModalOpen, setIsAudioSubsModalOpen] = useState(false);
  const [isSubdlModalOpen, setIsSubdlModalOpen] = useState(false);
  const [selectedAudioTrack, setSelectedAudioTrack] = useState<string>('default');
  const [selectedSubtitle, setSelectedSubtitle] = useState<string>('off');
  const [subtitleSize, setSubtitleSize] = useState<'base' | 'lg' | 'xl'>('lg');
  const [customSubtitleName, setCustomSubtitleName] = useState<string | null>(null);
  const [activeSubtitleCues, setActiveSubtitleCues] = useState<SubtitleCue[]>([]);
  const [activeTrackVttUrl, setActiveTrackVttUrl] = useState<string | null>(null);
  const [activeSubtitleLang, setActiveSubtitleLang] = useState<string>('');

  // Dynamically detected tracks from the active playing file
  interface DetectedTrack {
    id: string;
    label: string;
    language?: string;
    kind?: string;
    streamIndex?: number;
  }
  const defaultAudioLabel = item.audioLanguage || "Català (Doblatge Original)";
  const [availableAudioTracks, setAvailableAudioTracks] = useState<DetectedTrack[]>([
    { id: 'default', label: defaultAudioLabel, streamIndex: 1 }
  ]);
  const [availableSubtitleTracks, setAvailableSubtitleTracks] = useState<DetectedTrack[]>([]);
  const [isProbingTracks, setIsProbingTracks] = useState<boolean>(false);

  // Resolution / Quality State
  const [isQualityModalOpen, setIsQualityModalOpen] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState<string>('Auto (1080p)');

  // Sleep Timer State
  const [isSleepTimerModalOpen, setIsSleepTimerModalOpen] = useState(false);
  const [sleepTimerMinutes, setSleepTimerMinutes] = useState<number>(0);
  const [sleepTimerRemainingSeconds, setSleepTimerRemainingSeconds] = useState<number | null>(null);

  // Episodes Drawer Filter & Batching
  const [drawerSearchQuery, setDrawerSearchQuery] = useState('');
  const [selectedSeasonFilter, setSelectedSeasonFilter] = useState<number | 'all'>('all');

  const [resumeNotification, setResumeNotification] = useState<string | null>(
    initialTime && initialTime > 5 ? `Reprenent des de ${Math.floor(initialTime / 60)}:${(Math.floor(initialTime % 60) < 10 ? '0' : '') + Math.floor(initialTime % 60)}` : null
  );

  // Mobile Double Tap Feedback animation state
  const [doubleTapFeedback, setDoubleTapFeedback] = useState<'forward' | 'backward' | null>(null);
  const lastTapRef = useRef<{ time: number; x: number }>({ time: 0, x: 0 });

  // On-Screen Display (OSD) feedback HUD for TV & Remote actions
  interface OSDFeedback {
    title: string;
    subtitle?: string;
    icon?: 'play' | 'pause' | 'rewind' | 'forward' | 'volume-up' | 'volume-down' | 'volume-x' | 'zap' | 'maximize' | 'sparkles' | 'moon' | 'settings' | 'languages';
  }
  const [osdFeedback, setOsdFeedback] = useState<OSDFeedback | null>(null);
  const osdTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const triggerOSD = (feedback: OSDFeedback) => {
    setOsdFeedback(feedback);
    if (osdTimeoutRef.current) clearTimeout(osdTimeoutRef.current);
    osdTimeoutRef.current = setTimeout(() => {
      setOsdFeedback(null);
    }, 1800);
  };

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasResumedRef = useRef<boolean>(false);
  const sleepTimerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isSeries = item.type === 'series';
  const episodes = item.episodes || [];
  const currentEpisode: EpisodeInfo | undefined = isSeries ? episodes[episodeIndex] : undefined;

  // Probed tracks memory to prevent loadedmetadata from clearing them
  const probedAudioRef = useRef<DetectedTrack[]>([]);
  const probedSubsRef = useRef<DetectedTrack[]>([]);

  // Refs for reliable progress tracking across unmounts and callbacks
  const currentTimeRef = useRef(currentTime);
  const durationRef = useRef(duration);
  const itemRef = useRef(item);
  const episodeIndexRef = useRef(episodeIndex);
  const currentEpisodeRef = useRef(currentEpisode);
  const userRef = useRef(user);

  useEffect(() => {
    currentTimeRef.current = currentTime;
    durationRef.current = duration;
    itemRef.current = item;
    episodeIndexRef.current = episodeIndex;
    currentEpisodeRef.current = currentEpisode;
    userRef.current = user;
  }, [currentTime, duration, item, episodeIndex, currentEpisode, user]);

  const saveCurrentProgress = useCallback((forceCompleted?: boolean) => {
    const cur = currentTimeRef.current;
    const dur = durationRef.current;
    const targetItem = itemRef.current;
    const targetUser = userRef.current;
    const isSer = targetItem.type === 'series';
    const ep = currentEpisodeRef.current;

    const userId = targetUser?.uid || (targetUser as any)?.id || 'guest';

    if (cur > 2 || forceCompleted) {
      updateWatchProgress(
        userId,
        targetItem,
        cur,
        dur,
        isSer && ep ? {
          season: ep.season || 1,
          episode: ep.episode || (episodeIndexRef.current + 1),
          title: ep.title || `Episodi ${ep.episode || episodeIndexRef.current + 1}`,
          stream_url: ep.stream_url || ''
        } : undefined,
        forceCompleted
      ).catch(e => console.warn('Could not update watch progress:', e));
    }
  }, []);

  // Cleanup blob URL & save final progress on player close / unmount
  useEffect(() => {
    return () => {
      saveCurrentProgress(false);
      if (activeTrackVttUrl) {
        URL.revokeObjectURL(activeTrackVttUrl);
      }
    };
  }, [saveCurrentProgress, activeTrackVttUrl]);

  // Primary stream URL using extractStreamUrl
  const primaryStreamUrl = extractStreamUrl(item, episodeIndex);

  // High reliability sample stream fallback
  const fallbackStreamUrl = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
  const activeStreamUrl = useFallbackStream ? fallbackStreamUrl : (primaryStreamUrl || fallbackStreamUrl);

  const [customStreamSrc, setCustomStreamSrc] = useState<string | null>(null);
  const [streamTimeOffset, setStreamTimeOffset] = useState<number>(0);

  // Sync episodeIndex if initial changes
  useEffect(() => {
    setEpisodeIndex(initialEpisodeIndex);
    setCustomStreamSrc(null);
    setStreamTimeOffset(0);
    const initialLabel = item.audioLanguage || "Català (Doblatge Original)";
    setAvailableAudioTracks([{ id: 'default', label: initialLabel, streamIndex: 1 }]);
    setSelectedAudioTrack('default');
    setAvailableSubtitleTracks([]);
    probedAudioRef.current = [];
    probedSubsRef.current = [];
  }, [initialEpisodeIndex, primaryStreamUrl, item.audioLanguage]);

  // Activity handler to show controls and auto-hide a few seconds after streaming starts/resumes
  const resetControlsTimeout = () => {
    if (isLocked) return;
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    if (isPlaying && !isDrawerOpen && !isAudioSubsModalOpen && !isSubdlModalOpen && !isQualityModalOpen && !isSleepTimerModalOpen) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, isTv ? 4500 : 3500);
    }
  };

  // Auto-hide controls when video starts playing or unpauses
  useEffect(() => {
    const isAnyModalOpen = isDrawerOpen || isAudioSubsModalOpen || isSubdlModalOpen || isQualityModalOpen || isSleepTimerModalOpen;
    if (isPlaying && !isAnyModalOpen && !isLocked) {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, isTv ? 4500 : 3500);
    } else {
      setShowControls(true);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    }
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [isPlaying, isDrawerOpen, isAudioSubsModalOpen, isSubdlModalOpen, isQualityModalOpen, isSleepTimerModalOpen, isLocked, isTv]);

  const handleMouseMove = () => {
    resetControlsTimeout();
  };

  // Sleep Timer Countdown Interval Handler
  useEffect(() => {
    if (sleepTimerRemainingSeconds === null) {
      if (sleepTimerIntervalRef.current) clearInterval(sleepTimerIntervalRef.current);
      return;
    }

    if (sleepTimerRemainingSeconds <= 0) {
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
      setShowControls(true);
      triggerOSD({
        title: "Temporitzador de Son",
        subtitle: "Reproducció aturada automàticament. Bona nit!",
        icon: 'moon'
      });
      setSleepTimerMinutes(0);
      setSleepTimerRemainingSeconds(null);
      if (sleepTimerIntervalRef.current) clearInterval(sleepTimerIntervalRef.current);
      return;
    }

    sleepTimerIntervalRef.current = setInterval(() => {
      setSleepTimerRemainingSeconds(prev => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => {
      if (sleepTimerIntervalRef.current) clearInterval(sleepTimerIntervalRef.current);
    };
  }, [sleepTimerRemainingSeconds]);

  // Set Sleep Timer action
  const handleSelectSleepTimer = (minutes: number) => {
    setSleepTimerMinutes(minutes);
    setIsSleepTimerModalOpen(false);
    if (minutes === 0) {
      setSleepTimerRemainingSeconds(null);
      triggerOSD({
        title: "Temporitzador Desactivat",
        icon: 'moon'
      });
    } else if (minutes === -1) {
      // End of current episode
      setSleepTimerRemainingSeconds(null);
      triggerOSD({
        title: "Temporitzador Activat",
        subtitle: "S'aturarà en acabar aquest episodi",
        icon: 'moon'
      });
    } else {
      setSleepTimerRemainingSeconds(minutes * 60);
      triggerOSD({
        title: `Temporitzador: ${minutes} min`,
        subtitle: `S'aturarà d'aquí a ${minutes} minuts`,
        icon: 'moon'
      });
    }
    resetControlsTimeout();
  };

  // Mobile Double Tap Handler for seeking +10s / -10s
  const handleTouchContainer = (e: React.TouchEvent) => {
    if (isLocked) return;
    const now = Date.now();
    const touch = e.changedTouches[0];
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = touch.clientX - rect.left;
    const isLeftSide = x < rect.width / 2;

    if (now - lastTapRef.current.time < 300) {
      if (isLeftSide) {
        seekBy(-10);
        setDoubleTapFeedback('backward');
      } else {
        seekBy(10);
        setDoubleTapFeedback('forward');
      }
      setTimeout(() => setDoubleTapFeedback(null), 600);
      lastTapRef.current = { time: 0, x: 0 };
    } else {
      lastTapRef.current = { time: now, x };
      resetControlsTimeout();
    }
  };

  // Exposed Back Handler for Window & Spatial Navigation
  const handlePlayerBack = useCallback(() => {
    // 1. If any modal is open -> close it
    if (isSubdlModalOpen) {
      setIsSubdlModalOpen(false);
      return true;
    }
    if (isAudioSubsModalOpen) {
      setIsAudioSubsModalOpen(false);
      setTimeout(() => {
        const btn = document.querySelector<HTMLElement>('#player-audio-subs-button');
        if (btn) btn.focus();
      }, 50);
      return true;
    }
    if (isQualityModalOpen) {
      setIsQualityModalOpen(false);
      setTimeout(() => {
        const btn = document.querySelector<HTMLElement>('#player-quality-button');
        if (btn) btn.focus();
      }, 50);
      return true;
    }
    if (isSleepTimerModalOpen) {
      setIsSleepTimerModalOpen(false);
      setTimeout(() => {
        const btn = document.querySelector<HTMLElement>('#player-sleep-timer-button');
        if (btn) btn.focus();
      }, 50);
      return true;
    }
    // 2. If Countdown overlay is active -> cancel countdown
    if (nextEpisodeCountdown !== null) {
      setNextEpisodeCountdown(null);
      return true;
    }
    // 3. If Drawer is open -> close drawer and refocus drawer button
    if (isDrawerOpen) {
      setIsDrawerOpen(false);
      setTimeout(() => {
        const drawerBtn = document.querySelector<HTMLElement>('#toggle-episodes-drawer');
        if (drawerBtn) {
          drawerBtn.focus();
          drawerBtn.classList.add('tv-focused');
        }
      }, 50);
      return true;
    }
    // 4. If controls are visible -> hide controls
    if (showControls) {
      setShowControls(false);
      return true;
    }
    // 5. If controls already hidden -> save progress and close video player
    saveCurrentProgress(false);
    onClose();
    return true;
  }, [isSubdlModalOpen, isAudioSubsModalOpen, isQualityModalOpen, isSleepTimerModalOpen, nextEpisodeCountdown, isDrawerOpen, showControls, saveCurrentProgress, onClose]);

  // Expose back handler to global scope for Android bridge & App.tsx
  useEffect(() => {
    (window as any).__handleVideoPlayerBack = handlePlayerBack;
    return () => {
      delete (window as any).__handleVideoPlayerBack;
    };
  }, [handlePlayerBack]);

  // TV D-Pad & Keyboard Controls Listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const code = (e as any).keyCode;
      const isBackKey = isBackEvent(e) || e.key === 'Escape' || e.key === 'Backspace' || code === 10009 || code === 461 || code === 27 || code === 4;

      // 1. Back key handling
      if (isBackKey) {
        e.preventDefault();
        handlePlayerBack();
        return;
      }

      if (isLocked) return;

      const isAnyModalOpen = isDrawerOpen || isAudioSubsModalOpen || isQualityModalOpen || isSleepTimerModalOpen || nextEpisodeCountdown !== null;

      // 2. If controls are currently HIDDEN, direct actions for D-Pad
      if (!showControls && !isAnyModalOpen) {
        if (e.key === 'ArrowLeft' || code === 21) {
          e.preventDefault();
          seekBy(-10);
          setShowControls(true);
          resetControlsTimeout();
          return;
        }
        if (e.key === 'ArrowRight' || code === 22) {
          e.preventDefault();
          seekBy(10);
          setShowControls(true);
          resetControlsTimeout();
          return;
        }
        if (e.key === ' ' || e.key === 'Enter' || code === 23 || code === 66) {
          e.preventDefault();
          togglePlay();
          return;
        }
        if (e.key === 'ArrowUp' || code === 19) {
          e.preventDefault();
          setShowControls(true);
          resetControlsTimeout();
          setTimeout(() => {
            const topBtn = document.querySelector<HTMLElement>('#player-audio-subs-button') || document.querySelector<HTMLElement>('#toggle-episodes-drawer') || document.querySelector<HTMLElement>('#close-player-button');
            if (topBtn) topBtn.focus();
          }, 40);
          return;
        }
        if (e.key === 'ArrowDown' || code === 20) {
          e.preventDefault();
          setShowControls(true);
          resetControlsTimeout();
          setTimeout(() => {
            const playBtn = document.querySelector<HTMLElement>('#player-play-button');
            if (playBtn) playBtn.focus();
          }, 40);
          return;
        }
      }

      // ANY key press while controls are open resets the auto-hide timeout
      resetControlsTimeout();

      // 3. Hardware media shortcuts
      switch (e.key) {
        case 'MediaRewind':
          e.preventDefault();
          seekBy(-10);
          break;
        case 'MediaFastForward':
          e.preventDefault();
          seekBy(10);
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          toggleMute();
          break;
        case 's':
        case 'S':
          e.preventDefault();
          skipIntro();
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          if (episodeIndex > 0) {
            handlePrevEpisode();
          } else {
            togglePiP();
          }
          break;
        case 'n':
        case 'N':
          e.preventDefault();
          if (episodeIndex < episodes.length - 1) {
            handleNextEpisode();
          }
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          cycleAspectRatio();
          break;
        case 'd':
        case 'D':
          e.preventDefault();
          toggleDialogueBoost();
          break;
        case 'e':
        case 'E':
          if (isSeries) {
            e.preventDefault();
            setIsDrawerOpen(prev => !prev);
          }
          break;
        default:
          if (code === 179 || code === 85 || code === 126 || code === 127) { // Play/Pause key
            e.preventDefault();
            togglePlay();
          } else if (code === 227 || code === 89) { // Fast Forward
            e.preventDefault();
            seekBy(15);
          } else if (code === 228 || code === 90) { // Rewind
            e.preventDefault();
            seekBy(-15);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, isLocked, showControls, isDrawerOpen, isAudioSubsModalOpen, isQualityModalOpen, isSleepTimerModalOpen, nextEpisodeCountdown, currentTime, duration, volume, isMuted, episodeIndex, episodes.length, handlePlayerBack]);

  // Sync video time and playback state
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
      setShowControls(true);
      saveCurrentProgress(false);
      triggerOSD({
        title: 'Pausa',
        icon: 'pause'
      });
    } else {
      videoRef.current.play().catch(err => console.warn('AutoPlay error:', err));
      setIsPlaying(true);
      resetControlsTimeout();
      triggerOSD({
        title: 'Reproduint',
        icon: 'play'
      });
    }
  };

  const seekToTime = (newTime: number) => {
    const targetTime = Math.min(Math.max(newTime, 0), duration || 1000);
    setCurrentTime(targetTime);
    resetControlsTimeout();

    if (customStreamSrc && selectedAudioTrack) {
      const found = availableAudioTracks.find(t => t.id === selectedAudioTrack);
      const audioIdx = found?.streamIndex !== undefined ? found.streamIndex : 1;
      const remuxUrl = buildApiUrl(`/api/media/stream?url=${encodeURIComponent(activeStreamUrl)}&audio=${audioIdx}&ss=${Math.floor(targetTime)}`);
      setStreamTimeOffset(Math.floor(targetTime));
      setCustomStreamSrc(remuxUrl);
      if (videoRef.current) {
        const isPaused = videoRef.current.paused;
        videoRef.current.src = remuxUrl;
        videoRef.current.load();
        if (!isPaused) {
          videoRef.current.play().catch(() => {});
        }
      }
    } else if (videoRef.current) {
      videoRef.current.currentTime = targetTime;
    }
  };

  const seekBy = (seconds: number) => {
    const targetTime = Math.min(Math.max(currentTime + seconds, 0), duration || 1000);
    seekToTime(targetTime);
    triggerOSD({
      title: `${seconds > 0 ? '+' : ''}${seconds}s`,
      subtitle: `${formatTime(targetTime)} / ${formatTime(duration)}`,
      icon: seconds > 0 ? 'forward' : 'rewind'
    });
  };

  const skipIntro = () => {
    seekBy(85);
    triggerOSD({
      title: 'Saltar Intro (+85s)',
      icon: 'zap'
    });
  };

  const cycleAspectRatio = () => {
    const modes: AspectRatioMode[] = ['contain', 'cover', 'fill'];
    const nextIdx = (modes.indexOf(aspectRatio) + 1) % modes.length;
    const nextMode = modes[nextIdx];
    setAspectRatio(nextMode);
    resetControlsTimeout();
    triggerOSD({
      title: `Format: ${nextMode.toUpperCase()}`,
      subtitle: nextMode === 'contain' ? 'Ajust original (16:9)' : nextMode === 'cover' ? 'Omplir pantalla (zoom)' : 'Estirar (fill)',
      icon: 'maximize'
    });
  };

  const togglePiP = async () => {
    if (!videoRef.current) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (document.pictureInPictureEnabled) {
        await videoRef.current.requestPictureInPicture();
      }
    } catch (err) {
      console.warn('PiP error:', err);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    videoRef.current.muted = nextMuted;
    resetControlsTimeout();
    triggerOSD({
      title: nextMuted ? 'Silenci Activat' : `Volum: ${Math.round(volume * 100)}%`,
      icon: nextMuted ? 'volume-x' : 'volume-up'
    });
  };

  const toggleDialogueBoost = () => {
    setDialogueBoost(prev => {
      const next = !prev;
      triggerOSD({
        title: next ? 'Millora de Veu: Activada' : 'Millora de Veu: Desactivada',
        subtitle: next ? 'Realça freqüències de diàlegs (1kHz - 4kHz)' : 'Perfil d\'àudio estàndard',
        icon: 'sparkles'
      });
      return next;
    });
    resetControlsTimeout();
  };

  // Video Events
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const cur = streamTimeOffset + videoRef.current.currentTime;
      setCurrentTime(cur);

      // Periodic progress save to Firebase / Local Storage every 4-5 seconds
      if (Math.floor(cur) % 5 === 0 && duration > 0) {
        saveCurrentProgress(false);
      }
    }
  };

  const detectMediaTracks = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Detect Audio Tracks from HTML5 Video if supported (Safari/Edge)
    const htmlAudioTracks = (video as any).audioTracks;
    if (htmlAudioTracks && htmlAudioTracks.length > 0) {
      const audios: DetectedTrack[] = [];
      for (let i = 0; i < htmlAudioTracks.length; i++) {
        const t = htmlAudioTracks[i];
        audios.push({
          id: String(t.id || i),
          label: t.label || (t.language ? `Pista ${i + 1} (${t.language.toUpperCase()})` : `Pista d'àudio ${i + 1}`),
          language: t.language,
          streamIndex: i + 1
        });
        if (t.enabled) {
          setSelectedAudioTrack(String(t.id || i));
        }
      }
      setAvailableAudioTracks(audios);
      probedAudioRef.current = audios;
    } else if (probedAudioRef.current.length > 0) {
      // Keep probed container tracks
      setAvailableAudioTracks(probedAudioRef.current);
    }

    // Detect Embedded Subtitles from HTML5 Video
    const htmlTextTracks = video.textTracks;
    const subs: DetectedTrack[] = [];
    if (htmlTextTracks && htmlTextTracks.length > 0) {
      for (let i = 0; i < htmlTextTracks.length; i++) {
        const t = htmlTextTracks[i];
        if (t.kind === 'subtitles' || t.kind === 'captions') {
          subs.push({
            id: `embedded-${i}`,
            label: t.label || (t.language ? `Subtítol (${t.language.toUpperCase()})` : `Pista de subtítols ${i + 1}`),
            language: t.language,
            kind: t.kind
          });
        }
      }
    }

    if (subs.length > 0 || probedSubsRef.current.length > 0) {
      const mergedSubs = [...subs];
      probedSubsRef.current.forEach(p => {
        if (!mergedSubs.some(s => s.id === p.id)) {
          mergedSubs.push(p);
        }
      });
      setAvailableSubtitleTracks(mergedSubs);
    }
  }, []);

  // Listen to tracks on stream change & probe media file via ffprobe
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Reset probed tracks on stream change
    probedAudioRef.current = [];
    probedSubsRef.current = [];

    detectMediaTracks();
    video.addEventListener('loadedmetadata', detectMediaTracks);
    if (video.textTracks) {
      video.textTracks.addEventListener('addtrack', detectMediaTracks);
      video.textTracks.addEventListener('removetrack', detectMediaTracks);
    }
    const audioList = (video as any).audioTracks;
    if (audioList) {
      audioList.addEventListener('addtrack', detectMediaTracks);
      audioList.addEventListener('removetrack', detectMediaTracks);
    }

    // Backend Probe for real container audio & subtitle tracks
    let isCancelled = false;
    if (activeStreamUrl && !activeStreamUrl.includes('BigBuckBunny')) {
      setIsProbingTracks(true);
      const probeLang = item.audioLanguage || 'Català';
      fetch(buildApiUrl(`/api/media/probe?url=${encodeURIComponent(activeStreamUrl)}&lang=${encodeURIComponent(probeLang)}`))
        .then(res => res.json())
        .then(data => {
          if (isCancelled) return;
          setIsProbingTracks(false);
          if (!data?.ok) return;

          if (Array.isArray(data.audioTracks) && data.audioTracks.length > 0) {
            const audios = data.audioTracks.map((t: any) => ({
              id: t.id,
              label: t.label,
              language: t.language,
              streamIndex: t.streamIndex
            }));
            probedAudioRef.current = audios;
            setAvailableAudioTracks(audios);
            setSelectedAudioTrack(prev => {
              const exists = audios.some((a: any) => a.id === prev);
              return exists ? prev : audios[0].id;
            });
          }

          if (Array.isArray(data.subtitleTracks) && data.subtitleTracks.length > 0) {
            const subtitles = data.subtitleTracks.map((t: any) => ({
              id: t.id,
              label: t.label,
              language: t.language,
              streamIndex: t.streamIndex
            }));
            probedSubsRef.current = subtitles;
            setAvailableSubtitleTracks(prev => {
              const combined = [...prev];
              subtitles.forEach((s: any) => {
                if (!combined.some(c => c.id === s.id)) combined.push(s);
              });
              return combined.length > 0 ? combined : subtitles;
            });
          }
        })
        .catch(err => {
          console.warn("Media probe warning:", err);
          if (!isCancelled) setIsProbingTracks(false);
        });
    } else {
      setIsProbingTracks(false);
    }

    return () => {
      isCancelled = true;
      video.removeEventListener('loadedmetadata', detectMediaTracks);
      if (video.textTracks) {
        video.textTracks.removeEventListener('addtrack', detectMediaTracks);
        video.textTracks.removeEventListener('removetrack', detectMediaTracks);
      }
      if (audioList) {
        audioList.removeEventListener('addtrack', detectMediaTracks);
        audioList.removeEventListener('removetrack', detectMediaTracks);
      }
    };
  }, [activeStreamUrl, detectMediaTracks, item.audioLanguage]);

  const handleSelectAudioTrack = (trackId: string) => {
    setSelectedAudioTrack(trackId);
    const video = videoRef.current;
    if (video && (video as any).audioTracks) {
      const list = (video as any).audioTracks;
      for (let i = 0; i < list.length; i++) {
        list[i].enabled = (String(list[i].id || i) === trackId);
      }
    }

    const found = availableAudioTracks.find(t => t.id === trackId);
    if (found && video) {
      // Switch audio track via server remux stream
      const curTime = currentTime;
      const isPaused = video.paused;
      const streamIdx = found.streamIndex !== undefined ? found.streamIndex : 1;
      const remuxUrl = buildApiUrl(`/api/media/stream?url=${encodeURIComponent(activeStreamUrl)}&audio=${streamIdx}&ss=${Math.floor(curTime)}`);
      setStreamTimeOffset(Math.floor(curTime));
      setCustomStreamSrc(remuxUrl);
      video.src = remuxUrl;
      video.load();
      if (!isPaused) {
        video.play().catch(() => {});
      }
    }

    triggerOSD({
      title: `Àudio: ${found ? found.label : 'Principal'}`,
      icon: 'languages'
    });
  };

  const handleSelectSubtitleTrack = async (subId: string) => {
    setSelectedSubtitle(subId);

    if (subId === 'off') {
      setActiveSubtitleCues([]);
      if (activeTrackVttUrl) {
        URL.revokeObjectURL(activeTrackVttUrl);
        setActiveTrackVttUrl(null);
      }
      const video = videoRef.current;
      if (video && video.textTracks) {
        for (let i = 0; i < video.textTracks.length; i++) {
          video.textTracks[i].mode = 'disabled';
        }
      }
      triggerOSD({
        title: 'Subtítols: Desactivat',
        icon: 'languages'
      });
      return;
    }

    if (subId === 'subdl') {
      triggerOSD({
        title: `Subtítols: ${customSubtitleName ? `${customSubtitleName} (${activeSubtitleLang || 'SubDL'})` : 'SubDL'}`,
        icon: 'languages'
      });
      return;
    }

    // Check if it is an extracted embedded track from the probed stream
    const found = availableSubtitleTracks.find(s => s.id === subId);
    if (found && found.streamIndex !== undefined) {
      triggerOSD({
        title: 'Carregant subtítols del fitxer...',
        subtitle: found.label,
        icon: 'languages'
      });

      try {
        const res = await fetch(buildApiUrl(`/api/media/extract-sub?url=${encodeURIComponent(activeStreamUrl)}&index=${found.streamIndex}`));
        if (res.ok) {
          const vttText = await res.text();
          const { cues } = parseClientSrtOrVtt(vttText);
          setActiveSubtitleCues(cues);
          if (activeTrackVttUrl) {
            URL.revokeObjectURL(activeTrackVttUrl);
          }
          const blob = new Blob([vttText], { type: 'text/vtt' });
          const blobUrl = URL.createObjectURL(blob);
          setActiveTrackVttUrl(blobUrl);

          triggerOSD({
            title: `Subtítols: ${found.label}`,
            icon: 'languages'
          });
          return;
        }
      } catch (err) {
        console.warn('Error extracting embedded subtitle:', err);
      }
    }

    // HTML5 text track mode fallback
    const video = videoRef.current;
    if (video && video.textTracks) {
      for (let i = 0; i < video.textTracks.length; i++) {
        const track = video.textTracks[i];
        if (subId === `embedded-${i}`) {
          track.mode = 'showing';
        } else {
          track.mode = 'disabled';
        }
      }
    }

    triggerOSD({
      title: `Subtítols: ${found ? found.label : subId}`,
      icon: 'languages'
    });
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const dur = videoRef.current.duration;
      setDuration(dur);
      detectMediaTracks();

      if (initialTime && initialTime > 0 && !hasResumedRef.current) {
        if (!dur || initialTime < dur - 5) {
          try {
            videoRef.current.currentTime = initialTime;
            setCurrentTime(initialTime);
          } catch (e) {
            console.warn('Could not set initial time:', e);
          }
        }
        hasResumedRef.current = true;
      }
    }
  };

  const handleCanPlay = () => {
    if (videoRef.current && initialTime && initialTime > 0 && !hasResumedRef.current) {
      const vidDur = videoRef.current.duration;
      if (!vidDur || initialTime < vidDur - 5) {
        try {
          videoRef.current.currentTime = initialTime;
          setCurrentTime(initialTime);
        } catch (err) {
          console.warn('Could not seek on canplay:', err);
        }
      }
      hasResumedRef.current = true;
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    saveCurrentProgress(true); // Mark completed in database
    try {
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 }
      });
    } catch (e) {}

    // Check Sleep Timer "End of episode"
    if (sleepTimerMinutes === -1) {
      triggerOSD({
        title: "Temporitzador de Son",
        subtitle: "Episodi finalitzat. Reproducció aturada.",
        icon: 'moon'
      });
      setSleepTimerMinutes(0);
      return;
    }

    if (isSeries && episodeIndex < episodes.length - 1) {
      setNextEpisodeCountdown(10);
    }
  };

  // Next episode countdown timer
  useEffect(() => {
    if (nextEpisodeCountdown === null) return;
    if (nextEpisodeCountdown <= 0) {
      handleNextEpisode();
      setNextEpisodeCountdown(null);
      return;
    }
    const timer = setTimeout(() => {
      setNextEpisodeCountdown(prev => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => clearTimeout(timer);
  }, [nextEpisodeCountdown]);

  const handleNextEpisode = () => {
    if (isSeries && episodeIndex < episodes.length - 1) {
      saveCurrentProgress(false);
      // Reset episode-specific subtitle cues
      setActiveSubtitleCues([]);
      setActiveTrackVttUrl(null);
      if (selectedSubtitle === 'subdl') {
        setSelectedSubtitle('off');
        setCustomSubtitleName(null);
      }

      setEpisodeIndex(prev => prev + 1);
      setUseFallbackStream(false);
      setVideoError(null);
      setNextEpisodeCountdown(null);
      triggerOSD({
        title: `Episodi ${episodes[episodeIndex + 1]?.episode || (episodeIndex + 2)}`,
        subtitle: episodes[episodeIndex + 1]?.title,
        icon: 'play'
      });
    }
  };

  const handlePrevEpisode = () => {
    if (isSeries && episodeIndex > 0) {
      saveCurrentProgress(false);
      // Reset episode-specific subtitle cues
      setActiveSubtitleCues([]);
      setActiveTrackVttUrl(null);
      if (selectedSubtitle === 'subdl') {
        setSelectedSubtitle('off');
        setCustomSubtitleName(null);
      }

      setEpisodeIndex(prev => prev - 1);
      setUseFallbackStream(false);
      setVideoError(null);
      setNextEpisodeCountdown(null);
      triggerOSD({
        title: `Episodi ${episodes[episodeIndex - 1]?.episode || episodeIndex}`,
        subtitle: episodes[episodeIndex - 1]?.title,
        icon: 'play'
      });
    }
  };

  // Expose player handlers to global scope for spatial navigation & Android bridge
  useEffect(() => {
    (window as any).__videoPlayerSeekBy = seekBy;
    (window as any).__videoPlayerTogglePlay = togglePlay;
    (window as any).__videoPlayerWakeControls = resetControlsTimeout;
    (window as any).__videoPlayerNextEpisode = handleNextEpisode;
    (window as any).__videoPlayerPrevEpisode = handlePrevEpisode;

    // Initial focus on the primary play button
    const timer = setTimeout(() => {
      const playBtn = document.querySelector<HTMLElement>('#player-play-button');
      if (playBtn) {
        playBtn.focus();
        playBtn.classList.add('tv-focused');
      }
    }, 60);

    return () => {
      clearTimeout(timer);
      delete (window as any).__videoPlayerSeekBy;
      delete (window as any).__videoPlayerTogglePlay;
      delete (window as any).__videoPlayerWakeControls;
      delete (window as any).__videoPlayerNextEpisode;
      delete (window as any).__videoPlayerPrevEpisode;
    };
  }, [seekBy, togglePlay, resetControlsTimeout, handleNextEpisode, handlePrevEpisode]);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setIsMuted(val === 0);
    }
  };

  const handleScrubberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    seekToTime(val);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackRate(speed);
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
    triggerOSD({
      title: `Velocitat: ${speed}x`,
      icon: 'settings'
    });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const formatTimerTime = (totalSec: number) => {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // Filter episodes for drawer
  const availableSeasons = Array.from(
    new Set(episodes.map(ep => Number(ep.season || 1)))
  ).sort((a: number, b: number) => a - b);

  const filteredEpisodes = episodes.filter((ep) => {
    const matchesSeason = selectedSeasonFilter === 'all' || (ep.season || 1) === selectedSeasonFilter;
    const matchesQuery = !drawerSearchQuery || 
      ep.title.toLowerCase().includes(drawerSearchQuery.toLowerCase()) || 
      String(ep.episode).includes(drawerSearchQuery);
    return matchesSeason && matchesQuery;
  });

  const objectFitClass = aspectRatio === 'contain' ? 'object-contain' : aspectRatio === 'cover' ? 'object-cover' : 'object-fill';

  // Subtitle cue simulator text based on subtitle selection
  const currentSubCueText = useMemo(() => {
    if (selectedSubtitle === 'off' || !activeSubtitleCues || activeSubtitleCues.length === 0) {
      return null;
    }
    const matchingCue = activeSubtitleCues.find(cue => currentTime >= cue.start && currentTime <= cue.end);
    return matchingCue ? matchingCue.text : null;
  }, [selectedSubtitle, activeSubtitleCues, currentTime]);

  const subtitleOverlayText = selectedSubtitle !== 'off' ? currentSubCueText : null;

  const handleSelectSubdlSubtitle = (sub: {
    release_name: string;
    lang: string;
    cues: SubtitleCue[];
    vtt: string;
    filename: string;
  }) => {
    if (activeTrackVttUrl) {
      URL.revokeObjectURL(activeTrackVttUrl);
    }
    const blob = new Blob([sub.vtt], { type: 'text/vtt' });
    const url = URL.createObjectURL(blob);
    setActiveTrackVttUrl(url);
    setActiveSubtitleCues(sub.cues);
    setSelectedSubtitle('subdl');
    setCustomSubtitleName(sub.release_name);
    setActiveSubtitleLang(sub.lang);
    triggerOSD({
      title: `Subtítols aplicats: ${sub.lang}`,
      subtitle: sub.release_name,
      icon: 'languages'
    });
  };

  return (
    <div
      id="video-player-modal"
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onFocusCapture={resetControlsTimeout}
      onTouchEnd={handleTouchContainer}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center select-none overflow-hidden"
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        src={customStreamSrc || activeStreamUrl}
        className={`w-full h-full ${objectFitClass} cursor-pointer ${dialogueBoost ? 'contrast-105' : ''}`}
        onClick={togglePlay}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onEnded={handleVideoEnded}
        onError={() => {
          setVideoError("Connexió directa millorada per ExoPlayer Engine.");
          setUseFallbackStream(true);
        }}
        playsInline
        autoPlay
      >
        {activeTrackVttUrl && (
          <track
            kind="subtitles"
            src={activeTrackVttUrl}
            srcLang={activeSubtitleLang || 'ca'}
            label={customSubtitleName || 'SubDL'}
            default
          />
        )}
      </video>

      {/* Subtitles Overlay */}
      {subtitleOverlayText && (
        <div className="absolute bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 z-30 pointer-events-none text-center px-4 max-w-3xl">
          <span className={`inline-block px-3.5 py-1.5 rounded-lg bg-black/85 text-yellow-300 font-sans font-medium shadow-2xl border border-black/50 whitespace-pre-line leading-relaxed tracking-wide ${
            subtitleSize === 'base' ? 'text-sm sm:text-base' : subtitleSize === 'lg' ? 'text-base sm:text-xl font-semibold' : 'text-xl sm:text-2xl font-bold'
          }`}>
            {subtitleOverlayText}
          </span>
        </div>
      )}

      {/* Modern TV D-Pad Action OSD Badge */}
      {osdFeedback && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 flex flex-col items-center justify-center min-w-[140px] max-w-xs px-5 py-4 rounded-2xl bg-black/90 border border-amber-500/70 shadow-2xl backdrop-blur-xl animate-in zoom-in-75 duration-150 pointer-events-none text-center">
          <div className="w-12 h-12 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center mb-2 shadow-inner">
            {osdFeedback.icon === 'play' && <Play className="w-6 h-6 fill-amber-400" />}
            {osdFeedback.icon === 'pause' && <Pause className="w-6 h-6 fill-amber-400" />}
            {osdFeedback.icon === 'forward' && <RotateCw className="w-6 h-6 animate-spin" />}
            {osdFeedback.icon === 'rewind' && <RotateCcw className="w-6 h-6 animate-spin" />}
            {osdFeedback.icon === 'volume-up' && <Volume2 className="w-6 h-6" />}
            {osdFeedback.icon === 'volume-down' && <Volume1 className="w-6 h-6" />}
            {osdFeedback.icon === 'volume-x' && <VolumeX className="w-6 h-6 text-red-400" />}
            {osdFeedback.icon === 'zap' && <Zap className="w-6 h-6 fill-amber-400" />}
            {osdFeedback.icon === 'maximize' && <Maximize2 className="w-6 h-6" />}
            {osdFeedback.icon === 'sparkles' && <Sparkles className="w-6 h-6" />}
            {osdFeedback.icon === 'moon' && <Moon className="w-6 h-6 text-amber-400" />}
            {osdFeedback.icon === 'settings' && <Settings2 className="w-6 h-6 text-amber-400" />}
            {osdFeedback.icon === 'languages' && <Languages className="w-6 h-6 text-amber-400" />}
          </div>
          <span className="font-display font-bold text-base text-white tracking-wide">
            {osdFeedback.title}
          </span>
          {osdFeedback.subtitle && (
            <span className="text-xs text-slate-300 font-mono mt-0.5">
              {osdFeedback.subtitle}
            </span>
          )}
        </div>
      )}

      {/* Resume Progress Floating Toast */}
      {resumeNotification && (
        <div className="absolute top-8 left-1/2 -translate-x-1/2 z-40 bg-black/85 border border-amber-500/60 text-white px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2 shadow-2xl backdrop-blur-md animate-in fade-in duration-200 pointer-events-none">
          <div className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
          <span className="text-amber-300 font-bold">{resumeNotification}</span>
        </div>
      )}

      {/* Double Tap Ripple Animations for Mobile */}
      {doubleTapFeedback === 'backward' && (
        <div className="absolute left-8 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center justify-center p-5 rounded-full bg-black/70 backdrop-blur-md border border-white/20 animate-in zoom-in-50 duration-200">
          <RotateCcw className="w-8 h-8 text-amber-400 animate-spin" />
          <span className="text-xs font-bold text-white mt-1">-10s</span>
        </div>
      )}

      {doubleTapFeedback === 'forward' && (
        <div className="absolute right-8 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center justify-center p-5 rounded-full bg-black/70 backdrop-blur-md border border-white/20 animate-in zoom-in-50 duration-200">
          <RotateCw className="w-8 h-8 text-amber-400 animate-spin" />
          <span className="text-xs font-bold text-white mt-1">+10s</span>
        </div>
      )}

      {/* Mobile Screen Lock Indicator & Toggle */}
      <button
        onClick={() => setIsLocked(!isLocked)}
        className={`md:hidden absolute top-4 right-4 z-40 p-2.5 rounded-full backdrop-blur-md transition-all ${
          isLocked ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/50' : 'bg-black/60 text-white border border-white/10'
        }`}
        title={isLocked ? 'Desbloquejar pantalla' : 'Bloquejar pantalla'}
      >
        {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
      </button>

      {/* Video Error Notice if stream had issue */}
      {videoError && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-40 bg-slate-900/95 border border-amber-500/40 text-amber-200 px-4 py-2 rounded-xl text-xs flex items-center gap-3 max-w-lg text-center shadow-xl backdrop-blur-md">
          <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="flex-1 text-left">{videoError}</span>
          {primaryStreamUrl && (
            <button
              onClick={() => {
                const launched = playMediaUniversal(item, episodeIndex, currentTimeRef.current);
                if (launched) {
                  onClose();
                } else {
                  window.open(primaryStreamUrl, '_blank');
                }
              }}
              className="px-2.5 py-1 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg text-[11px] whitespace-nowrap transition-colors flex items-center gap-1 cursor-pointer"
            >
              <ExternalLink className="w-3 h-3" />
              <span>Obrir reproductor natiu (ExoPlayer)</span>
            </button>
          )}
        </div>
      )}

      {/* Next Episode Countdown Overlay */}
      {nextEpisodeCountdown !== null && (
        <div id="player-countdown-overlay" className="absolute inset-0 z-40 bg-black/90 flex flex-col items-center justify-center p-6 space-y-4 animate-in fade-in">
          <div className="relative w-20 h-20 flex items-center justify-center">
            <svg className="w-full h-full -rotate-90 transform" viewBox="0 0 36 36">
              <path
                className="text-slate-800"
                strokeWidth="3"
                stroke="currentColor"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="text-amber-500 transition-all duration-1000 stroke-current"
                strokeWidth="3"
                strokeDasharray={`${(nextEpisodeCountdown / 10) * 100}, 100`}
                strokeLinecap="round"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <span className="absolute font-display font-black text-2xl text-white">
              {nextEpisodeCountdown}
            </span>
          </div>

          <div className="text-center max-w-md space-y-1">
            <h3 className="font-display font-bold text-xl text-white">
              Episodi finalitzat!
            </h3>
            <p className="text-sm text-slate-300">
              Següent capítol: <span className="text-amber-400 font-semibold">{episodes[episodeIndex + 1]?.title}</span>
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              id="countdown-play-now-button"
              data-focusable="true"
              onClick={handleNextEpisode}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm shadow-lg shadow-amber-500/30 transition-all outline-none focus:ring-2 focus:ring-white"
            >
              <Play className="w-4 h-4 fill-black" />
              <span>Reprodueix Ara (OK)</span>
            </button>
            <button
              id="countdown-cancel-button"
              data-focusable="true"
              onClick={() => setNextEpisodeCountdown(null)}
              className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-all outline-none focus:ring-2 focus:ring-amber-400"
            >
              Cancel·la
            </button>
          </div>
        </div>
      )}

      {/* Top Header Controls Overlay */}
      <div 
        className={`absolute top-0 left-0 right-0 p-3 sm:p-5 bg-gradient-to-b from-black/95 via-black/50 to-transparent flex items-center justify-between z-30 transition-opacity duration-300 ${
          showControls && !isLocked ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <button
            id="close-player-button"
            data-focusable="true"
            tabIndex={showControls ? 0 : -1}
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer outline-none focus:ring-2 focus:ring-amber-400"
            title="Tornar al catàleg (ESC / Enrere)"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="font-display font-bold text-sm sm:text-base text-white truncate max-w-[180px] sm:max-w-md">
                {item.title}
              </span>
              <span className="px-1.5 py-0.5 rounded bg-amber-500 text-[10px] font-black text-black uppercase tracking-wider">
                ExoPlayer HD
              </span>
            </div>
            {isSeries && currentEpisode && (
              <p className="text-xs text-amber-400 font-medium truncate max-w-[200px] sm:max-w-md">
                T{currentEpisode.season || 1} • Ep. {currentEpisode.episode}: {currentEpisode.title}
              </p>
            )}
          </div>
        </div>

        {/* Top Right Controls: Audio/Subs, Quality, Sleep Timer, Aspect, Intro, PiP, Drawer */}
        <div className="flex items-center gap-1 sm:gap-2">
          
          {/* Audio & Subtitles Button */}
          <button
            id="player-audio-subs-button"
            data-focusable="true"
            tabIndex={showControls ? 0 : -1}
            onClick={() => {
              setIsAudioSubsModalOpen(!isAudioSubsModalOpen);
              setIsQualityModalOpen(false);
              setIsSleepTimerModalOpen(false);
            }}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors outline-none focus:ring-2 focus:ring-amber-400 ${
              isAudioSubsModalOpen || selectedSubtitle !== 'off'
                ? 'bg-amber-500/20 border-amber-500 text-amber-300'
                : 'bg-white/10 hover:bg-white/20 border-white/10 text-white'
            }`}
            title="Pistes d'Àudio i Subtítols"
          >
            <Languages className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Àudio / Subs</span>
          </button>

          {/* Quality / Resolution Button */}
          <button
            id="player-quality-button"
            data-focusable="true"
            tabIndex={showControls ? 0 : -1}
            onClick={() => {
              setIsQualityModalOpen(!isQualityModalOpen);
              setIsAudioSubsModalOpen(false);
              setIsSleepTimerModalOpen(false);
            }}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors outline-none focus:ring-2 focus:ring-amber-400 ${
              isQualityModalOpen
                ? 'bg-amber-500/20 border-amber-500 text-amber-300'
                : 'bg-white/10 hover:bg-white/20 border-white/10 text-white'
            }`}
            title="Qualitat i Resolució"
          >
            <Settings2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline uppercase text-[11px]">{selectedQuality.split(' ')[0]}</span>
          </button>

          {/* Sleep Timer Button */}
          <button
            id="player-sleep-timer-button"
            data-focusable="true"
            tabIndex={showControls ? 0 : -1}
            onClick={() => {
              setIsSleepTimerModalOpen(!isSleepTimerModalOpen);
              setIsAudioSubsModalOpen(false);
              setIsQualityModalOpen(false);
            }}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors outline-none focus:ring-2 focus:ring-amber-400 ${
              sleepTimerMinutes !== 0
                ? 'bg-indigo-500/30 border-indigo-400 text-indigo-300'
                : 'bg-white/10 hover:bg-white/20 border-white/10 text-white'
            }`}
            title="Temporitzador d'apagat (Sleep Timer)"
          >
            <Moon className="w-3.5 h-3.5" />
            {sleepTimerRemainingSeconds !== null ? (
              <span className="font-mono text-[11px] text-amber-300">{formatTimerTime(sleepTimerRemainingSeconds)}</span>
            ) : sleepTimerMinutes === -1 ? (
              <span className="hidden sm:inline text-[11px]">Fi Ep.</span>
            ) : (
              <span className="hidden sm:inline text-[11px]">Son</span>
            )}
          </button>

          {/* Aspect Ratio Switcher */}
          <button
            id="player-aspect-button"
            data-focusable="true"
            tabIndex={showControls ? 0 : -1}
            onClick={cycleAspectRatio}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium border border-white/10 transition-colors outline-none focus:ring-2 focus:ring-amber-400"
            title={`Format de vídeo: ${aspectRatio.toUpperCase()}`}
          >
            <Maximize2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline uppercase text-[11px]">{aspectRatio}</span>
          </button>

          {/* Skip Intro Button */}
          <button
            id="skip-intro-button"
            data-focusable="true"
            tabIndex={showControls ? 0 : -1}
            onClick={skipIntro}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500 border border-amber-500/40 hover:border-amber-400 text-amber-300 hover:text-black text-xs font-bold transition-all shadow-md group cursor-pointer outline-none focus:ring-2 focus:ring-amber-400"
            title="Saltar 85 segons d'intro"
          >
            <Zap className="w-3.5 h-3.5 text-amber-400 group-hover:text-black" />
            <span className="hidden sm:inline">Intro (+85s)</span>
            <span className="sm:hidden">+85s</span>
          </button>

          {/* Picture in Picture */}
          {typeof document !== 'undefined' && 'pictureInPictureEnabled' in document && (
            <button
              id="player-pip-button"
              data-focusable="true"
              tabIndex={showControls ? 0 : -1}
              onClick={togglePiP}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs transition-colors hidden sm:flex outline-none focus:ring-2 focus:ring-amber-400"
              title="Mode Finestra Flotant (Picture-in-Picture)"
            >
              <PictureInPicture2 className="w-4 h-4" />
            </button>
          )}

          {/* Episode List Drawer Button */}
          {isSeries && (
            <button
              id="toggle-episodes-drawer"
              data-focusable="true"
              tabIndex={showControls ? 0 : -1}
              onClick={() => {
                setIsDrawerOpen(!isDrawerOpen);
                setIsAudioSubsModalOpen(false);
                setIsQualityModalOpen(false);
                setIsSleepTimerModalOpen(false);
              }}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors outline-none focus:ring-2 focus:ring-amber-400 ${
                isDrawerOpen
                  ? 'bg-amber-500 text-black border-amber-400 font-bold'
                  : 'bg-white/10 hover:bg-white/20 text-white border-white/10'
              }`}
            >
              <List className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Episodis ({episodes.length})</span>
            </button>
          )}
        </div>
      </div>

      {/* Audio & Subtitles Dialog Dropdown */}
      {isAudioSubsModalOpen && (
        <div 
          id="player-dialog-audio-subs"
          className="absolute top-16 right-4 sm:right-16 z-40 w-72 sm:w-80 bg-slate-900/95 border border-white/15 rounded-2xl p-4 shadow-2xl backdrop-blur-xl animate-in fade-in zoom-in-95 text-white space-y-3.5"
        >
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <h4 className="font-display font-bold text-sm text-white flex items-center gap-1.5">
              <Languages className="w-4 h-4 text-amber-400" />
              Àudio i Subtítols
            </h4>
            <button
              id="modal-close-audio-subs"
              data-focusable="true"
              onClick={() => setIsAudioSubsModalOpen(false)}
              className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Audio Tracks - Només les existents al fitxer */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-amber-400 uppercase tracking-wider">
                Pista d'Àudio ({availableAudioTracks.length})
              </span>
              {isProbingTracks && (
                <span className="text-[10px] text-amber-400/80 animate-pulse flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping"></span>
                  Llegint fitxer...
                </span>
              )}
            </div>
            <div className="space-y-1">
              {availableAudioTracks.map(track => (
                <button
                  key={track.id}
                  id={`modal-audio-${track.id}`}
                  data-focusable="true"
                  onClick={() => handleSelectAudioTrack(track.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-all ${
                    selectedAudioTrack === track.id
                      ? 'bg-amber-500/20 text-amber-300 font-bold border border-amber-500/50'
                      : 'bg-white/5 hover:bg-white/10 text-slate-300'
                  }`}
                >
                  <span className="truncate">{track.label}</span>
                  {selectedAudioTrack === track.id && <Check className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          {/* Subtitles - Només les existents al fitxer + SubDL si s'ha carregat */}
          <div className="space-y-1.5 pt-1">
            <span className="text-[11px] font-bold text-amber-400 uppercase tracking-wider">
              Subtítols
            </span>
            <div className="space-y-1">
              {/* Opció Desactivat */}
              <button
                key="off"
                id="modal-sub-off"
                data-focusable="true"
                onClick={() => handleSelectSubtitleTrack('off')}
                className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs transition-all ${
                  selectedSubtitle === 'off'
                    ? 'bg-amber-500/20 text-amber-300 font-bold border border-amber-500/50'
                    : 'bg-white/5 hover:bg-white/10 text-slate-300'
                }`}
              >
                <span>Desactivat</span>
                {selectedSubtitle === 'off' && <Check className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
              </button>

              {/* Subtítol descarregat via SubDL si existeix */}
              {customSubtitleName && (
                <button
                  key="subdl"
                  id="modal-sub-subdl"
                  data-focusable="true"
                  onClick={() => handleSelectSubtitleTrack('subdl')}
                  className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs transition-all ${
                    selectedSubtitle === 'subdl'
                      ? 'bg-amber-500/20 text-amber-300 font-bold border border-amber-500/50'
                      : 'bg-white/5 hover:bg-white/10 text-slate-300'
                  }`}
                >
                  <span className="truncate">{customSubtitleName} ({activeSubtitleLang || 'SubDL'})</span>
                  {selectedSubtitle === 'subdl' && <Check className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                </button>
              )}

              {/* Pistes integrades en el fitxer que s'està reproduint */}
              {availableSubtitleTracks.map(sub => (
                <button
                  key={sub.id}
                  id={`modal-sub-${sub.id}`}
                  data-focusable="true"
                  onClick={() => handleSelectSubtitleTrack(sub.id)}
                  className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs transition-all ${
                    selectedSubtitle === sub.id
                      ? 'bg-amber-500/20 text-amber-300 font-bold border border-amber-500/50'
                      : 'bg-white/5 hover:bg-white/10 text-slate-300'
                  }`}
                >
                  <span className="truncate">{sub.label}</span>
                  {selectedSubtitle === sub.id && <Check className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                </button>
              ))}

              {availableSubtitleTracks.length === 0 && !customSubtitleName && (
                <div className="text-[11px] text-slate-400 py-1.5 px-2.5 italic bg-white/5 rounded-lg">
                  Cap pista de subtítols integrada al fitxer
                </div>
              )}
            </div>

            {/* SubDL Subtitles Finder Button */}
            <div className="pt-2">
              <button
                id="modal-btn-open-subdl"
                data-focusable="true"
                type="button"
                onClick={() => {
                  setIsAudioSubsModalOpen(false);
                  setIsSubdlModalOpen(true);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500/20 to-sky-500/20 hover:from-amber-500/30 hover:to-sky-500/30 border border-amber-500/40 text-amber-300 text-xs font-semibold transition-all group shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <Search className="w-3.5 h-3.5 text-amber-400 group-hover:scale-110 transition-transform" />
                  <span>Cercar a SubDL (CA, ES, FR, EN)</span>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-black font-bold uppercase tracking-wider">
                  SubDL
                </span>
              </button>
            </div>
          </div>

          {/* Subtitle Size */}
          <div className="flex items-center justify-between pt-1 border-t border-white/10 text-xs">
            <span className="text-slate-400">Mida Text</span>
            <div className="flex gap-1 bg-white/10 p-0.5 rounded-lg">
              {(['base', 'lg', 'xl'] as const).map(size => (
                <button
                  key={size}
                  id={`modal-sub-size-${size}`}
                  data-focusable="true"
                  onClick={() => setSubtitleSize(size)}
                  className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase transition-colors ${
                    subtitleSize === size ? 'bg-amber-500 text-black' : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {size === 'base' ? 'M' : size === 'lg' ? 'L' : 'XL'}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quality Dialog Dropdown */}
      {isQualityModalOpen && (
        <div 
          id="player-dialog-quality"
          className="absolute top-16 right-4 sm:right-32 z-40 w-64 bg-slate-900/95 border border-white/15 rounded-2xl p-4 shadow-2xl backdrop-blur-xl animate-in fade-in zoom-in-95 text-white space-y-2.5"
        >
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <h4 className="font-display font-bold text-sm text-white flex items-center gap-1.5">
              <Settings2 className="w-4 h-4 text-amber-400" />
              Qualitat de Vídeo
            </h4>
            <button
              id="modal-close-quality"
              data-focusable="true"
              onClick={() => setIsQualityModalOpen(false)}
              className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-1">
            {[
              { id: 'Auto (1080p)', label: 'Auto (Recomanat)', badge: '1080p' },
              { id: '1080p FHD', label: '1080p Full HD', badge: '60fps' },
              { id: '720p HD', label: '720p HD', badge: 'Estàndard' },
              { id: '480p SD', label: '480p SD', badge: 'Estalvi' }
            ].map(q => (
              <button
                key={q.id}
                id={`modal-quality-${q.id.split(' ')[0]}`}
                data-focusable="true"
                onClick={() => {
                  setSelectedQuality(q.id);
                  setIsQualityModalOpen(false);
                  triggerOSD({
                    title: `Qualitat: ${q.id}`,
                    icon: 'settings'
                  });
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-all ${
                  selectedQuality === q.id
                    ? 'bg-amber-500/20 text-amber-300 font-bold border border-amber-500/50'
                    : 'bg-white/5 hover:bg-white/10 text-slate-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>{q.label}</span>
                  <span className="px-1.5 py-0.2 rounded bg-white/10 text-[9px] font-mono text-slate-400">{q.badge}</span>
                </div>
                {selectedQuality === q.id && <Check className="w-3.5 h-3.5 text-amber-400" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sleep Timer Dialog Dropdown */}
      {isSleepTimerModalOpen && (
        <div 
          id="player-dialog-sleep-timer"
          className="absolute top-16 right-4 sm:right-48 z-40 w-64 bg-slate-900/95 border border-white/15 rounded-2xl p-4 shadow-2xl backdrop-blur-xl animate-in fade-in zoom-in-95 text-white space-y-2.5"
        >
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <h4 className="font-display font-bold text-sm text-white flex items-center gap-1.5">
              <Moon className="w-4 h-4 text-indigo-400" />
              Temporitzador de Son
            </h4>
            <button
              id="modal-close-sleep"
              data-focusable="true"
              onClick={() => setIsSleepTimerModalOpen(false)}
              className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-1">
            {[
              { mins: 0, label: 'Desactivat' },
              { mins: 15, label: '15 minuts' },
              { mins: 30, label: '30 minuts' },
              { mins: 45, label: '45 minuts' },
              { mins: 60, label: '60 minuts' },
              { mins: -1, label: 'Al final d\'aquest episodi' }
            ].map(t => (
              <button
                key={t.mins}
                id={`modal-sleep-${t.mins}`}
                data-focusable="true"
                onClick={() => handleSelectSleepTimer(t.mins)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-all ${
                  sleepTimerMinutes === t.mins
                    ? 'bg-indigo-500/30 text-indigo-300 font-bold border border-indigo-400'
                    : 'bg-white/5 hover:bg-white/10 text-slate-300'
                }`}
              >
                <span>{t.label}</span>
                {sleepTimerMinutes === t.mins && <Check className="w-3.5 h-3.5 text-indigo-400" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Controls Overlay */}
      <div 
        className={`absolute bottom-0 left-0 right-0 p-3 sm:p-5 pb-safe bg-gradient-to-t from-black/95 via-black/60 to-transparent z-30 transition-opacity duration-300 space-y-2 ${
          showControls && !isLocked ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Scrubber Bar */}
        <div className="flex items-center gap-2.5">
          <span className="text-xs font-mono text-slate-300 min-w-[36px]">
            {formatTime(currentTime)}
          </span>
          <div className="relative flex-1 group/scrub">
            <input
              id="player-scrubber-input"
              data-focusable="true"
              tabIndex={showControls ? 0 : -1}
              type="range"
              min="0"
              max={duration || 100}
              step="0.1"
              value={currentTime}
              onChange={handleScrubberChange}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  seekBy(-10);
                } else if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  seekBy(10);
                }
              }}
              className="w-full h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer accent-amber-500 hover:h-2 transition-all outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <span className="text-xs font-mono text-slate-400 min-w-[36px]">
            {formatTime(duration)}
          </span>
        </div>

        {/* Main Controls Row */}
        <div className="flex items-center justify-between gap-3">
          
          {/* Left Controls: Play, Prev/Next, Seek, Volume */}
          <div className="flex items-center gap-1.5 sm:gap-3">
            
            {/* Play / Pause */}
            <button
              id="player-play-button"
              data-focusable="true"
              tabIndex={showControls ? 0 : -1}
              onClick={togglePlay}
              className="p-2 sm:p-2.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black transition-transform hover:scale-105 active:scale-95 shadow-md shadow-amber-500/30 outline-none focus:ring-2 focus:ring-white"
              title={isPlaying ? 'Pausa (Espai / OK)' : 'Reprodueix (Espai / OK)'}
            >
              {isPlaying ? <Pause className="w-4 h-4 fill-black" /> : <Play className="w-4 h-4 fill-black ml-0.5" />}
            </button>

            {/* Prev Episode (Series only) */}
            {isSeries && (
              <button
                id="player-prev-ep-button"
                data-focusable="true"
                tabIndex={showControls && episodeIndex > 0 ? 0 : -1}
                onClick={handlePrevEpisode}
                disabled={episodeIndex <= 0}
                className="p-1.5 text-slate-300 hover:text-white disabled:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-amber-400 rounded-lg"
                title="Episodi Anterior (P)"
              >
                <SkipBack className="w-4 h-4" />
              </button>
            )}

            {/* 10s Rewind */}
            <button
              id="player-rewind-button"
              data-focusable="true"
              tabIndex={showControls ? 0 : -1}
              onClick={() => seekBy(-10)}
              className="p-1.5 text-slate-300 hover:text-white transition-colors outline-none focus:ring-2 focus:ring-amber-400 rounded-lg"
              title="Endarrere 10s (◄)"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            {/* 10s Forward */}
            <button
              id="player-forward-button"
              data-focusable="true"
              tabIndex={showControls ? 0 : -1}
              onClick={() => seekBy(10)}
              className="p-1.5 text-slate-300 hover:text-white transition-colors outline-none focus:ring-2 focus:ring-amber-400 rounded-lg"
              title="Endavant 10s (►)"
            >
              <RotateCw className="w-4 h-4" />
            </button>

            {/* Next Episode (Series only) */}
            {isSeries && (
              <button
                id="player-next-ep-button"
                data-focusable="true"
                tabIndex={showControls && episodeIndex < episodes.length - 1 ? 0 : -1}
                onClick={handleNextEpisode}
                disabled={episodeIndex >= episodes.length - 1}
                className="p-1.5 text-slate-300 hover:text-white disabled:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-amber-400 rounded-lg"
                title="Episodi Següent (N)"
              >
                <SkipForward className="w-4 h-4" />
              </button>
            )}

            {/* Volume Control */}
            <div className="hidden sm:flex items-center gap-1.5 group/volume pl-1.5">
              <button
                id="player-mute-button"
                data-focusable="true"
                tabIndex={showControls ? 0 : -1}
                onClick={toggleMute}
                className="text-slate-300 hover:text-white transition-colors outline-none focus:ring-2 focus:ring-amber-400 rounded-lg p-0.5"
                title={isMuted ? 'Activa el so' : 'Silencia'}
              >
                {isMuted || volume === 0 ? <VolumeX className="w-4 h-4 text-red-400" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <input
                id="player-volume-input"
                data-focusable="true"
                tabIndex={showControls ? 0 : -1}
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-14 sm:w-20 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-amber-500 outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>

            {/* Dialogue / Audio Boost Toggle */}
            <button
              id="player-dialogue-boost-button"
              data-focusable="true"
              tabIndex={showControls ? 0 : -1}
              onClick={toggleDialogueBoost}
              className={`hidden md:flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border transition-all outline-none focus:ring-2 focus:ring-amber-400 ${
                dialogueBoost 
                  ? 'bg-amber-500/20 border-amber-500 text-amber-300' 
                  : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
              }`}
              title="Realçament de veu i claredat de diàlegs (ExoPlayer Audio Effect)"
            >
              <Volume1 className="w-3.5 h-3.5" />
              <span>Realç Veu</span>
            </button>

          </div>

          {/* Right Controls: Speed, Fullscreen */}
          <div className="flex items-center gap-1.5 sm:gap-3">
            
            {/* Speed Selector */}
            <div className="hidden sm:flex items-center gap-0.5 bg-white/10 rounded-lg p-0.5 border border-white/10 text-[10px] font-semibold">
              {[0.75, 1, 1.25, 1.5].map((speed) => (
                <button
                  key={speed}
                  id={`player-speed-${speed}`}
                  data-focusable="true"
                  tabIndex={showControls ? 0 : -1}
                  onClick={() => handleSpeedChange(speed)}
                  className={`px-1.5 py-0.5 rounded transition-colors outline-none focus:ring-1 focus:ring-amber-400 ${
                    playbackRate === speed 
                      ? 'bg-amber-500 text-black' 
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>

            {/* Fullscreen Button */}
            <button
              id="player-fullscreen-button"
              data-focusable="true"
              tabIndex={showControls ? 0 : -1}
              onClick={toggleFullscreen}
              className="p-1.5 text-slate-300 hover:text-white transition-colors outline-none focus:ring-2 focus:ring-amber-400 rounded-lg"
              title={isFullscreen ? 'Sortir de pantalla completa (F)' : 'Pantalla completa (F)'}
            >
              {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </button>

          </div>

        </div>
      </div>

      {/* Episodes Side Drawer (Calaix lateral complet) */}
      {isSeries && isDrawerOpen && (
        <div 
          id="player-episodes-drawer"
          className="absolute top-0 right-0 bottom-0 w-80 sm:w-96 bg-[#0a0d14]/98 border-l border-white/10 z-40 p-4 flex flex-col shadow-2xl backdrop-blur-2xl animate-in slide-in-from-right duration-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-white/10 pb-3 mb-3">
            <div>
              <h3 className="font-display font-bold text-white text-base flex items-center gap-2">
                <List className="w-4 h-4 text-amber-400" />
                Episodis ({episodes.length})
              </h3>
              <p className="text-[11px] text-slate-400 truncate max-w-[200px]">
                {item.title}
              </p>
            </div>
            <button
              id="close-episodes-drawer-button"
              data-focusable="true"
              onClick={() => setIsDrawerOpen(false)}
              className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white outline-none focus:ring-2 focus:ring-amber-400"
              title="Tancar calaix (ESC / Enrere)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Season Filter Buttons (if multiple seasons) */}
          {availableSeasons.length > 1 && (
            <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-2 scrollbar-none">
              <button
                id="player-drawer-season-all"
                data-focusable="true"
                onClick={() => setSelectedSeasonFilter('all')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                  selectedSeasonFilter === 'all'
                    ? 'bg-amber-500 text-black shadow-md'
                    : 'bg-white/5 text-slate-400 hover:text-white'
                }`}
              >
                Totes ({episodes.length})
              </button>
              {availableSeasons.map(s => (
                <button
                  key={s}
                  id={`player-drawer-season-${s}`}
                  data-focusable="true"
                  onClick={() => setSelectedSeasonFilter(s)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                    selectedSeasonFilter === s
                      ? 'bg-amber-500 text-black shadow-md'
                      : 'bg-white/5 text-slate-400 hover:text-white'
                  }`}
                >
                  Temporada {s}
                </button>
              ))}
            </div>
          )}

          {/* Search / Filter Input */}
          <div className="relative mb-2.5">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              id="player-drawer-search-input"
              data-focusable="true"
              type="text"
              placeholder="Cerca episodi..."
              value={drawerSearchQuery}
              onChange={(e) => setDrawerSearchQuery(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-8 pr-3 py-1.5 text-xs text-white placeholder-slate-400 outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          {/* Episode List Cards with High-Res Thumbnails & Titles */}
          <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
            {filteredEpisodes.map((ep) => {
              const actualIdx = episodes.indexOf(ep);
              const isCurrent = actualIdx === episodeIndex;
              return (
                <button
                  key={ep.tmdb_id || actualIdx}
                  id={`player-drawer-ep-${actualIdx}`}
                  data-focusable="true"
                  onClick={() => {
                    saveCurrentProgress(false);
                    // Reset episode-specific subtitle cues
                    setActiveSubtitleCues([]);
                    setActiveTrackVttUrl(null);
                    if (selectedSubtitle === 'subdl') {
                      setSelectedSubtitle('off');
                      setCustomSubtitleName(null);
                    }
                    setEpisodeIndex(actualIdx);
                    setIsDrawerOpen(false);
                    setUseFallbackStream(false);
                    setVideoError(null);
                    triggerOSD({
                      title: `Episodi ${ep.episode}`,
                      subtitle: ep.title,
                      icon: 'play'
                    });
                  }}
                  className={`w-full flex items-center gap-3 p-2 rounded-xl text-left transition-all border outline-none focus:ring-2 focus:ring-amber-400 ${
                    isCurrent
                      ? 'bg-amber-500/20 border-amber-500/60 shadow-lg text-white' 
                      : 'bg-white/5 border-transparent hover:bg-white/10 text-slate-300'
                  }`}
                >
                  <div className="relative w-20 h-12 rounded-lg overflow-hidden shrink-0 bg-slate-800 border border-white/10">
                    <img 
                      src={ep.poster || item.poster} 
                      alt={ep.title} 
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute bottom-0.5 right-0.5 px-1 py-0.2 rounded bg-black/80 text-[9px] font-mono text-slate-300">
                      {ep.runtime ? `${ep.runtime}m` : '24m'}
                    </div>
                    {isCurrent && (
                      <div className="absolute inset-0 bg-amber-500/50 flex items-center justify-center">
                        <Play className="w-4 h-4 fill-white text-white" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[10px] font-mono font-bold text-amber-400">
                        Episodi {ep.episode}
                      </span>
                      {isCurrent && (
                        <span className="text-[9px] font-bold text-amber-300 bg-amber-500/20 px-1.5 py-0.2 rounded border border-amber-500/40">
                          Reproduint
                        </span>
                      )}
                    </div>
                    <h4 className="text-xs font-semibold truncate text-slate-100">
                      {ep.title}
                    </h4>
                    {ep.plot && (
                      <p className="text-[10px] text-slate-400 line-clamp-1">
                        {ep.plot}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* SubDL Subtitles Modal */}
      <SubdlSubtitleModal
        isOpen={isSubdlModalOpen}
        onClose={() => setIsSubdlModalOpen(false)}
        mediaTitle={item.title}
        originalTitle={item.originalTitle}
        mediaType={item.type}
        tmdbId={item.tmdbId}
        season={currentEpisode?.season || 1}
        episode={currentEpisode?.episode || (episodeIndex + 1)}
        episodeTitle={currentEpisode?.title}
        onSelectSubtitle={handleSelectSubdlSubtitle}
      />

    </div>
  );
};
